import React, { useEffect, Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ConfigProvider, Layout, Spin } from 'antd';
import { HelmetProvider } from 'react-helmet-async';
import 'antd/dist/reset.css';
import './App.css';
import { trackPageView, trackSessionStart, trackSessionEnd } from './utils/analytics';
import { trackCampaignLanding, extractUTMParameters } from './utils/utmTracking';
import { initPerformanceOptimizations, unregisterServiceWorkers } from './utils/performance';

import { AuthProvider } from './contexts/AuthContext';
import { HeaderProvider, useHeader } from './contexts/HeaderContext';
import { Header, Footer } from './components/common/layout';
// Import pages directly to avoid lazy loading issues for now
import HeroLandingPage from './pages/public/HeroLandingPage';
import FreeInspectWeedPage from './pages/public/FreeInspectWeedPage';
import QuotePage from './pages/public/QuotePage';
import ServicesPage from './pages/public/ServicesPage';
import AboutPage from './pages/public/AboutPage';
import SignupPage from './pages/public/SignupPage';
import WorkshopPage from './pages/public/WorkshopPage';
import PaymentSuccessPage from './pages/public/PaymentSuccessPage';
import EmailMarketingPage from './pages/marketing/EmailMarketingPage';
import ReferralProgramPage from './pages/marketing/ReferralProgramPage';
import FAQPage from './pages/public/FAQPage';
import SitemapPage from './pages/public/SitemapPage';
import HowWeWorkPage from './pages/public/HowWeWorkPage';
import TermsOfServicePage from './pages/public/TermsOfServicePage';
import PrivacyPolicyPage from './pages/public/PrivacyPolicyPage';
import UserDashboard from './components/dashboard/user/UserDashboard';
import AnalyticsDashboard from './components/admin/analytics/AnalyticsDashboard';
import AdminRoute from './components/admin/management/AdminRoute';
import AdminLayout from './components/admin/dashboard/AdminLayout';


const { Content } = Layout;

// Loading component for Suspense
const LoadingSpinner = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100vh',
    flexDirection: 'column',
    gap: '16px'
  }}>
    <Spin size="large" />
    <div>Loading...</div>
  </div>
);

// Component to check for campaign-specific UTM parameters
function CampaignDetector() {
  const location = useLocation();

  useEffect(() => {
    // Only check on home page
    if (location.pathname === '/') {
      const utmParams = extractUTMParameters();

      // Check for the specific free-inspect-weed campaign
      if (
        utmParams.utm_source === 'door-to-door' &&
        utmParams.utm_medium === 'hangtag' &&
        utmParams.utm_campaign === 'free-inspect-weed' &&
        utmParams.utm_term === 'october-2025'
      ) {
        // Store campaign info for the session; DO NOT reload
        localStorage.setItem('show_campaign_page', 'true');
        localStorage.setItem('campaign_type', 'free-inspect-weed');
        localStorage.setItem('campaign_utm_params', JSON.stringify(utmParams));
      } 
      // Check for the flyer-1 campaign
      else if (
        utmParams.utm_source === 'flyer' &&
        utmParams.utm_medium === 'specific-date-junk-tree-trimming-power-washing-gutter-cleaning' &&
        utmParams.utm_campaign === 'flyer-1' &&
        utmParams.utm_term === 'october'
      ) {
        // Store campaign info for the session; DO NOT reload
        localStorage.setItem('show_campaign_page', 'true');
        localStorage.setItem('campaign_type', 'flyer-1');
        localStorage.setItem('campaign_utm_params', JSON.stringify(utmParams));
      } 
      else {
        // Clear campaign flags if not a specific campaign
        localStorage.removeItem('show_campaign_page');
        localStorage.removeItem('campaign_type');
        localStorage.removeItem('campaign_utm_params');
      }
    }
  }, [location]);

  return null;
}

// Component to track page views and scroll to top
function PageTracker() {
  const location = useLocation();

  useEffect(() => {
    const pageName = location.pathname === '/' ? 'home' : location.pathname.substring(1);
    const pageTitle = getPageTitle(location.pathname);
    
    trackPageView(pageName, pageTitle);
    
    // Scroll to top without animation when page changes
    window.scrollTo(0, 0);
  }, [location]);

  return null;
}

// Helper function to get page titles
function getPageTitle(pathname) {
  const titles = {
    '/': 'HSP - Home Services',
    '/QuotePage': 'Get Your Quote - HSP',
    '/services': 'Our Services - HSP',
    '/about': 'About Us - HSP',
    '/workshops': 'Workshops - HSP',
    '/email-marketing': 'Email Marketing - HSP',
    '/referral-program': 'Referral Program - HSP',
    '/signup': 'Sign Up - HSP',
    '/dashboard': 'User Dashboard - HSP',
    '/admin': 'Admin Dashboard - HSP',
    '/analytics': 'Analytics Dashboard - HSP',
    '/faq': 'FAQ - HSP',
    '/how-we-work': 'How We Work - HSP',
    '/terms-of-service': 'Terms of Service - HSP',
    '/privacy-policy': 'Privacy Policy - HSP'
  };
  return titles[pathname] || 'HSP - Home Services';
}


// Component to conditionally render header
function ConditionalHeader() {
  const location = useLocation();
  
  // Don't show header on admin and analytics pages
  if (location.pathname.startsWith('/admin') || location.pathname === '/analytics') {
    return null;
  }
  
  return <Header />;
}

// Component to conditionally render footer
function ConditionalFooter() {
  const location = useLocation();
  
  // Don't show footer on admin and analytics pages
  if (location.pathname.startsWith('/admin') || location.pathname === '/analytics') {
    return null;
  }
  
  return <Footer />;
}

// Component to conditionally render home page based on campaign
function ConditionalHomePage() {
  const [showCampaign, setShowCampaign] = useState(false);

  useEffect(() => {
    // Check if we should show the campaign page
    const shouldShowCampaign = localStorage.getItem('show_campaign_page') === 'true';
    setShowCampaign(shouldShowCampaign);
  }, []);

  // Use FreeInspectWeedPage for both campaigns - it handles the content dynamically
  return showCampaign ? <FreeInspectWeedPage /> : <HeroLandingPage />;
}

// Component to conditionally style content
function ConditionalContent({ children }) {
  const location = useLocation();
  
  // No top padding for admin and analytics pages since they handle their own layout
  let paddingTop = '0';
  if (location.pathname !== '/admin' && location.pathname !== '/analytics') {
    paddingTop = '0'; // Pages will handle their own header spacing
  }
  
  return (
    <Content style={{ paddingTop, transition: 'padding-top 0.3s ease' }}>
      {children}
    </Content>
  );
}

function App() {
  // Track session start when app loads
  useEffect(() => {
    trackSessionStart();
    
    // Initialize performance optimizations
    initPerformanceOptimizations();
    // Unregister all service workers and clear caches
    unregisterServiceWorkers();
    
    // Track UTM campaign landing with a small delay to ensure analytics is initialized
    setTimeout(() => {
      trackCampaignLanding();
    }, 1000);
    
    // Track session end when user leaves
    const handleBeforeUnload = () => {
      const sessionDuration = Date.now() - window.sessionStartTime;
      trackSessionEnd(sessionDuration);
    };
    
    window.sessionStartTime = Date.now();
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#FD865C',
          colorSuccess: '#52c41a',
          colorWarning: '#faad14',
          colorError: '#ff4d4f',
          borderRadius: 8,
          fontFamily: 'Futura, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
      }}
    >
      <HelmetProvider>
        <AuthProvider>
          <HeaderProvider>
            <Router>
            <CampaignDetector />
            <PageTracker />
            <Layout className="App" style={{ minHeight: '100vh' }}>
              <ConditionalHeader />
              <ConditionalContent>
                <Suspense fallback={<LoadingSpinner />}>
                  <Routes>
                    <Route path="/" element={<ConditionalHomePage />} />
                    <Route path="/quote" element={<QuotePage />} />
                    <Route path="/services" element={<ServicesPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/workshops" element={<WorkshopPage />} />
                    <Route path="/email-marketing" element={<EmailMarketingPage />} />
                    <Route path="/referral-program" element={<ReferralProgramPage />} />
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="/payment-success" element={<PaymentSuccessPage />} />
                    <Route path="/dashboard" element={<UserDashboard />} />
                    <Route path="/admin" element={
                      <AdminRoute>
                        <AdminLayout />
                      </AdminRoute>
                    } />
                    <Route path="/admin/quotes" element={
                      <AdminRoute>
                        <AdminLayout />
                      </AdminRoute>
                    } />
                    <Route path="/admin/users" element={
                      <AdminRoute>
                        <AdminLayout />
                      </AdminRoute>
                    } />
                    <Route path="/admin/settings" element={
                      <AdminRoute>
                        <AdminLayout />
                      </AdminRoute>
                    } />
                    <Route path="/analytics" element={
                      <AdminRoute>
                        <AdminLayout />
                      </AdminRoute>
                    } />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/sitemap" element={<SitemapPage />} />
                    <Route path="/how-we-work" element={<HowWeWorkPage />} />
                    <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  </Routes>
                </Suspense>
              </ConditionalContent>
              <ConditionalFooter />
            </Layout>
          </Router>
          </HeaderProvider>
        </AuthProvider>
      </HelmetProvider>
    </ConfigProvider>
  );
}

export default App;